import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ImagePlus, Images, Link2, Loader2, Star, Trash2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';

import { mediaAPI } from '../lib/api';
import CloudinaryMediaLibraryDialog from './CloudinaryMediaLibraryDialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';


const DEFAULT_MAX_GALLERY_IMAGES = 8;

const previewUrl = (url) => {
  if (!url || !url.includes('/image/upload/')) return url;
  return url.replace('/image/upload/', '/image/upload/f_auto,q_auto:eco,c_fill,g_auto,w_360,h_450/');
};

const withDefaultAlt = (asset, productName) => ({
  ...asset,
  alt: asset.alt || productName || 'URBNSW product image',
});

export default function ProductImageManager({
  featuredImage = null,
  galleryImages = [],
  productName = '',
  onChange,
  disabled = false,
  maxGalleryImages = DEFAULT_MAX_GALLERY_IMAGES,
  singleImage = false,
  title = 'Product images',
  description = 'Upload from your computer or reuse an image already stored in Cloudinary.',
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [link, setLink] = useState('');
  const [importing, setImporting] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const galleryLimit = singleImage ? 0 : Math.max(0, Number(maxGalleryImages ?? DEFAULT_MAX_GALLERY_IMAGES));

  const publish = (featured, gallery) => {
    onChange({
      featured: featured ? withDefaultAlt(featured, productName) : null,
      gallery: singleImage ? [] : gallery.slice(0, galleryLimit).map((image, index) => ({
        ...withDefaultAlt(image, productName),
        sort_order: index,
      })),
    });
  };

  const remainingCapacity = () => {
    if (singleImage) return 1;
    return (featuredImage ? 0 : 1) + Math.max(0, galleryLimit - galleryImages.length);
  };

  const addAssets = (assets) => {
    const normalized = (assets || []).filter((asset) => asset?.url).map((asset) => withDefaultAlt(asset, productName));
    if (!normalized.length) return;
    if (singleImage) {
      publish(normalized[0], []);
      return;
    }

    let featured = featuredImage;
    let gallery = [...galleryImages];
    const pending = [...normalized];
    if (!featured && pending.length) featured = pending.shift();
    const availableGallery = Math.max(0, galleryLimit - gallery.length);
    if (pending.length > availableGallery) {
      toast.warning(`A maximum of ${galleryLimit} gallery image${galleryLimit === 1 ? '' : 's'} is allowed`);
    }
    gallery = [...gallery, ...pending.slice(0, availableGallery)];
    publish(featured, gallery);
  };

  const handleFiles = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selectedFiles.length) return;
    const capacity = remainingCapacity();
    if (!singleImage && capacity === 0) {
      toast.warning('Remove an image before uploading another one');
      return;
    }
    const files = singleImage ? selectedFiles.slice(0, 1) : selectedFiles.slice(0, capacity);
    if (!singleImage && files.length < selectedFiles.length) {
      toast.warning(`Only ${capacity} more image${capacity === 1 ? '' : 's'} can be added`);
    }

    setUploading(true);
    setProgress(0);
    const uploaded = [];
    try {
      for (let index = 0; index < files.length; index += 1) {
        const asset = await mediaAPI.uploadFile(files[index], (fileProgress) => {
          setProgress(Math.round(((index + fileProgress / 100) / files.length) * 100));
        });
        uploaded.push(asset);
      }
      addAssets(uploaded);
      toast.success(`${uploaded.length} image${uploaded.length === 1 ? '' : 's'} uploaded to Cloudinary`);
    } catch (error) {
      toast.error(error.response?.data?.error?.message || error.response?.data?.detail || error.message || 'Image upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const importLink = async () => {
    if (!link.trim()) return;
    if (!singleImage && featuredImage && galleryImages.length >= galleryLimit) {
      toast.warning('Remove an image before importing another one');
      return;
    }
    setImporting(true);
    try {
      const response = await mediaAPI.importUrl(link.trim(), productName);
      addAssets([response.data]);
      setLink('');
      toast.success('Image imported into Cloudinary');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cloudinary could not import this image link');
    } finally {
      setImporting(false);
    }
  };

  const changeAlt = (location, index, alt) => {
    if (location === 'featured') {
      publish({ ...featuredImage, alt }, galleryImages);
      return;
    }
    const gallery = galleryImages.map((image, imageIndex) => imageIndex === index ? { ...image, alt } : image);
    publish(featuredImage, gallery);
  };

  const removeImage = (location, index) => {
    if (location === 'featured') {
      const [nextFeatured, ...remaining] = galleryImages;
      publish(nextFeatured || null, remaining);
      return;
    }
    publish(featuredImage, galleryImages.filter((_, imageIndex) => imageIndex !== index));
  };

  const makeFeatured = (index) => {
    const selected = galleryImages[index];
    const gallery = galleryImages.filter((_, imageIndex) => imageIndex !== index);
    if (featuredImage) gallery.unshift(featuredImage);
    publish(selected, gallery.slice(0, galleryLimit));
  };

  const moveGallery = (index, direction) => {
    const destination = index + direction;
    if (destination < 0 || destination >= galleryImages.length) return;
    const gallery = [...galleryImages];
    [gallery[index], gallery[destination]] = [gallery[destination], gallery[index]];
    publish(featuredImage, gallery);
  };

  const renderAsset = (asset, location, index = 0) => (
    <div key={asset.public_id || asset.url} className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="relative aspect-[4/5] overflow-hidden rounded-md bg-slate-100">
        <img src={previewUrl(asset.url)} alt={asset.alt || productName} className="h-full w-full object-cover" />
        {location === 'featured' && !singleImage && (
          <span className="absolute left-2 top-2 flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
            <Star className="h-3 w-3" /> Main
          </span>
        )}
        {singleImage && (
          <span className="absolute left-2 top-2 rounded bg-purple-600 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">Variation</span>
        )}
      </div>
      <div className="mt-3 space-y-2">
        <Label className="text-xs">Image alt text</Label>
        <Input
          value={asset.alt || ''}
          onChange={(event) => changeAlt(location, index, event.target.value)}
          placeholder={productName || 'Describe the product image'}
          disabled={disabled}
          className="h-8 text-xs"
        />
        <div className="flex flex-wrap gap-1">
          {location === 'gallery' && (
            <Button type="button" size="sm" variant="outline" onClick={() => makeFeatured(index)} disabled={disabled} className="h-7 px-2 text-[11px]">
              <Star className="h-3 w-3" /> Make main
            </Button>
          )}
          {location === 'gallery' && (
            <>
              <Button type="button" size="icon" variant="outline" onClick={() => moveGallery(index, -1)} disabled={disabled || index === 0} className="h-7 w-7"><ChevronLeft className="h-3 w-3" /></Button>
              <Button type="button" size="icon" variant="outline" onClick={() => moveGallery(index, 1)} disabled={disabled || index === galleryImages.length - 1} className="h-7 w-7"><ChevronRight className="h-3 w-3" /></Button>
            </>
          )}
          <Button type="button" size="icon" variant="outline" onClick={() => removeImage(location, index)} disabled={disabled} className="h-7 w-7 text-red-600 hover:text-red-700">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );

  const capacity = remainingCapacity();

  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="font-medium text-slate-900">{title}</p>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" multiple={!singleImage} onChange={handleFiles} className="hidden" />
          <Button type="button" variant="outline" onClick={() => setLibraryOpen(true)} disabled={disabled || (!singleImage && capacity === 0)}>
            <Images /> Choose from Cloudinary
          </Button>
          <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={disabled || uploading || (!singleImage && capacity === 0)} className="bg-indigo-600 hover:bg-indigo-700">
            {uploading ? <Loader2 className="animate-spin" /> : <UploadCloud />}
            {uploading ? `Uploading ${progress}%` : singleImage && featuredImage ? 'Replace image' : 'Upload images'}
          </Button>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Link2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={link} onChange={(event) => setLink(event.target.value)} placeholder="Or import a public HTTPS image link" type="url" className="pl-9" disabled={disabled || importing} />
        </div>
        <Button type="button" variant="outline" onClick={importLink} disabled={disabled || importing || !link.trim()}>
          {importing ? <Loader2 className="animate-spin" /> : <Link2 />} Import
        </Button>
      </div>

      {!featuredImage && galleryImages.length === 0 ? (
        <div className="flex min-h-32 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white text-center text-slate-500">
          <ImagePlus className="mb-2 h-7 w-7" />
          <p className="text-sm">No image selected</p>
          <p className="text-xs">{singleImage ? 'Choose the image customers see when this variation is selected.' : 'The first image becomes the main image.'}</p>
        </div>
      ) : (
        <div className={`grid gap-3 ${singleImage ? 'max-w-xs grid-cols-1' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
          {featuredImage ? renderAsset(featuredImage, 'featured') : null}
          {!singleImage ? galleryImages.map((image, index) => renderAsset(image, 'gallery', index)) : null}
        </div>
      )}
      <p className="text-[11px] text-slate-500">JPG, PNG, WebP or AVIF · maximum 10 MB each{singleImage ? ' · one variation image' : ` · up to ${galleryLimit} gallery images`}</p>

      <CloudinaryMediaLibraryDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        multiple={!singleImage}
        maxSelection={singleImage ? 1 : Math.max(1, capacity)}
        title={singleImage ? 'Choose variation image from Cloudinary' : 'Choose product images from Cloudinary'}
        onSelect={(assets) => addAssets(Array.isArray(assets) ? assets : [assets])}
      />
    </div>
  );
}
